import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faUsers, faChartLine, faRobot, faVideo, faImage } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

const gatherDataImageStyle = {
    width: '100%',
    height: '200px',
    objectFit: 'contain',
    display: 'block',
    margin: '0 auto'
};

function PublisherSocialSchedulerPage() {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <>
            <Helmet prioritizeSeoTags>
                <meta charSet="utf-8" />
                <title>Automate Your Games Social Media  Marketing | Glitch Gaming</title>
                <meta
                    name="description"
                    content="Maximize your game's exposure with AI-driven social media management. Save time and increase engagement effortlessly."
                />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Automate Your Games Social Media  Marketing | Glitch" />
                <meta property="og:description" content="Maximize your game's exposure with AI-driven social media management. Save time and increase engagement effortlessly." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/social_scheduler_banner.webp" />
            </Helmet>
            <Header />

            {/* Hero Section */}
            <section className="social-hero text-white">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start">
                            <h1 className="display-4 fw-bold mb-4 pt-5">
                                Automate Your Social Media <span className="text-gradient">Marketing</span>
                            </h1>
                            <p className="lead mb-5">Save 10+ hours weekly with AI-powered social media management tailored for game developers</p>
                            <div className="cta-group">
                                <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-primary btn-lg me-3 mb-3">
                                    Get Started  <FontAwesomeIcon icon={faRobot} className="ms-2" />
                                </Link>
                                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                                    Book A Demo
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="hero-visual mt-5 mt-lg-0 pt-5">
                                <img
                                    src="/assets/images/publishers/top_social_platforms.png"
                                    alt="Social Scheduler Preview"
                                    className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                                />

                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Value Propositions */}
            <section className="value-propositions py-5 bg-light">
                <div className="container text-black">
                    <h2 className="text-center mb-5 text-black">Why Game Developers Love Us</h2>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="value-card h-100 p-4 rounded-4 shadow-sm">
                                <div className="icon-wrapper bg-primary">
                                    <FontAwesomeIcon icon={faBullhorn} className="fa-2x" />
                                </div>
                                <h3 className="text-black mt-3">3x More Engagement</h3>
                                <p className="text-muted">AI-optimized posting schedule maximizes audience reach</p>
                                <ul className="value-list">
                                    <li>Optimal posting times</li>
                                    <li>Platform-specific formatting</li>
                                    <li>Hashtag optimization</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100 p-4 rounded-4 shadow-sm">
                                <div className="icon-wrapper bg-success">
                                    <FontAwesomeIcon icon={faUsers} className="fa-2x" />
                                </div>
                                <h3 className="text-black mt-3">Audience Growth</h3>
                                <p className="text-muted">Build loyal communities across platforms</p>
                                <ul className="value-list">
                                    <li>Cross-platform analytics</li>
                                    <li>Follower demographics</li>
                                    <li>Engagement tracking</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100 p-4 rounded-4 shadow-sm">
                                <div className="icon-wrapper bg-danger">
                                    <FontAwesomeIcon icon={faImage} className="fa-2x" />
                                </div>
                                <h3 className="text-black mt-3">Content Creation</h3>
                                <p className="text-muted">Transform updates into engaging posts</p>
                                <ul className="value-list">
                                    <li>Auto-resizing media</li>
                                    <li>Template library</li>
                                    <li>Brand consistency tools</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

           
{/* Success Metrics Section - Dark Theme */}
<section className="success-metrics py-5 bg-dark-gradient">
    <div className="container">
        <div className="row align-items-center">
            <div className="col-lg-6">
                <h2 className="display-5 fw-bold mb-4 text-white">Proven Results for Game Devs</h2>
                <p className="lead mb-4 text-light">In an industry where <span className="text-primary">90% of indie titles fail</span> to make over $10k, automated marketing gives small teams a fighting chance.</p>
                <div className="metric-highlight bg-primary-gradient text-white p-4 rounded-3 mb-4 border-0">
                    <p className="mb-0 fs-4"><strong>32%</strong> average wishlist-to-sales conversion <span className="d-block text-white-75">(vs. industry standard of 10%)</span></p>
                </div>
                <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-light btn-lg px-4">
                    See How It Works <FontAwesomeIcon icon={faChartLine} className="ms-2" />
                </Link>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
                <div className="metric-grid">
                    <div className="metric-card bg-dark-soft p-4 rounded-4 border border-secondary">
                        <h3 className="text-primary mb-3">500+</h3>
                        <p className="text-light mb-0">Daily wishlists</p>
                    </div>
                    <div className="metric-card bg-dark-soft p-4 rounded-4 border border-secondary">
                        <h3 className="text-success mb-3">$0.19</h3>
                        <p className="text-light mb-0">Cost per wishlist <span className="text-white-50">(from $3.00)</span></p>
                    </div>
                    <div className="metric-card bg-dark-soft p-4 rounded-4 border border-secondary">
                        <h3 className="text-info mb-3">Top 3%</h3>
                        <p className="text-light mb-0">Of Steam launches</p>
                    </div>
                    <div className="metric-card bg-dark-soft p-4 rounded-4 border border-secondary">
                        <h3 className="text-warning mb-3">10+ Hours</h3>
                        <p className="text-light mb-0">Saved weekly</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>

            {/* Workflow Section */}
            <section className="workflow-steps py-5 text-black">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold mb-3 text-black">Simple 3-Step Automation</h2>
                        <p className="lead">Set it and forget it - we handle the rest</p>
                    </div>

                    <div className="row g-5 position-relative">
                        {/* Step 1 */}
                        <div className="col-lg-4">
                            <div className="step-card bg-white p-4 rounded-4 shadow-lg h-100">
                                <div className="step-header d-flex align-items-center mb-4">
                                    <div className="step-number bg-primary text-white rounded-circle">1</div>
                                    <h3 className="h4 mb-0 ms-3 text-black">Submit Update</h3>
                                </div>
                                <img
                                    src="/assets/images/publishers/scheduler_submit.png"
                                    alt="Submit Content"
                                    className="img-fluid rounded-3 mb-3"
                                    style={{ height: '200px', objectFit: 'cover', width: '100%' }}
                                />
                                <p className="text-muted">Share quick updates about your game</p>
                                <div className="step-features">
                                    <div className="d-flex align-items-center mb-2">
                                        <i className="fas fa-comment me-2 text-primary"></i>
                                        <span>Text, Images, or Video</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="col-lg-4">
                            <div className="step-card bg-white p-4 rounded-4 shadow-lg h-100">
                                <div className="step-header d-flex align-items-center mb-4">
                                    <div className="step-number bg-success text-white rounded-circle">2</div>
                                    <h3 className="h4 mb-0 ms-3 text-black">AI Optimization</h3>
                                </div>
                                <img
                                    src="/assets/images/publishers/scheduler_results.png"
                                    alt="AI Processing"
                                    className="img-fluid rounded-3 mb-3"
                                    style={{ height: '200px', objectFit: 'cover', width: '100%' }}
                                />
                                <p className="text-muted">We transform content for each platform</p>
                                <div className="step-features">
                                    <div className="d-flex align-items-center mb-2">
                                        <i className="fas fa-magic me-2 text-success"></i>
                                        <span>Automatic Formatting</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="col-lg-4">
                            <div className="step-card bg-white p-4 rounded-4 shadow-lg h-100">
                                <div className="step-header d-flex align-items-center mb-4">
                                    <div className="step-number bg-danger text-white rounded-circle">3</div>
                                    <h3 className="h4 mb-0 ms-3 text-black">Track Results</h3>
                                </div>
                                <img
                                    src="/assets/images/publishers/graph_up.png"
                                    alt="Analytics Dashboard"
                                    className="img-fluid rounded-3 mb-3"
                                    style={{ height: '200px', objectFit: 'cover', width: '100%' }}
                                />
                                <p className="text-muted">Monitor performance across platforms</p>
                                <div className="step-features">
                                    <div className="d-flex align-items-center mb-2">
                                        <i className="fas fa-chart-line me-2 text-danger"></i>
                                        <span>Real-time Analytics</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Platform Integration */}
            <section className="platform-integration py-5 bg-dark">
                <div className="container text-center">
                    <h2 className="text-white mb-5">Integrated With All Major Platforms</h2>
                    <div className="row g-4 justify-content-center">
                        {['youtube', 'tiktok', 'reddit', 'facebook', 'twitter', 'instagram'].map((platform, index) => (
                            <div className="col-4 col-md-2" key={index}>
                                <div className="platform-card bg-white rounded-3 p-3 shadow-sm">
                                    <img
                                        src={`/assets/images/creators/${platform}.png`}
                                        alt={platform}
                                        className="img-fluid"
                                        style={{ height: '40px', width: 'auto' }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Analytics Showcase */}
            <section className="analytics-showcase py-5 bg-light">
                <div className="container">
                    <h2 className="text-center mb-5 text-black">Actionable Marketing Insights</h2>
                    <div className="row g-4">
                        {[
                            { id: 'chart_statistics', title: 'Engagement Statistics' },
                            { id: 'chart_comparison', title: 'Platform Comparisons' },
                            { id: 'chart_follower_growth', title: 'Follower Growth' },
                            { id: 'chart_progression', title: 'Campaign Progression' }
                        ].map((chart, index) => (
                            <div className="col-md-6" key={index}>
                                <div className="analytics-card bg-white p-3 rounded-3 shadow-sm h-100">
                                    <h4 className="text-black text-center mb-3">{chart.title}</h4>
                                    <img
                                        src={`/assets/images/publishers/${chart.id}.png`}
                                        alt="Analytics Chart"
                                        className="img-fluid"
                                        style={gatherDataImageStyle}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="final-cta py-5 bg-gradient">
                <div className="container text-center">
                    <h2 className="text-white mb-4">Start Automating Your Social Media Today</h2>
                    <p className="lead text-white mb-5">Join 1,200+ game developers saving time and boosting engagement</p>
                    <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-light btn-lg px-5 py-3 me-3">
                        Get Started Free
                    </Link>
                    <div className="trust-badges mt-4">
                        <div className="badge-item d-inline-block bg-white rounded-pill px-3 py-1 me-2">
                            <i className="fas fa-shield-alt text-primary me-2"></i>
                            <span className="text-dark">Secure & Private</span>
                        </div>
                    </div>
                </div>
            </section>

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .social-hero {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                }

                .text-gradient {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .value-card {
                    transition: transform 0.3s ease;
                    border: 1px solid rgba(0,0,0,0.05);
                }

                .value-card:hover {
                    transform: translateY(-10px);
                }

                .icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .workflow-steps {
                    background: linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%);
                }

                .step-number {
                    width: 50px;
                    height: 50px;
                    font-size: 1.5rem;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .progress-line {
                    height: 4px;
                    width: 60%;
                    left: 20%;
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                }

                .platform-card {
                    transition: transform 0.3s ease;
                }

                .platform-card:hover {
                    transform: scale(1.05);
                }

                .analytics-card {
                    transition: box-shadow 0.3s ease;
                }

                .analytics-card:hover {
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1) !important;
                }

                .bg-gradient {
                    background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
                }

                @media (max-width: 768px) {
                    .social-hero {
                        padding: 60px 0;
                    }
                    
                    .display-4 {
                        font-size: 2.5rem;
                    }
                }

                .bg-dark-gradient {
    background: linear-gradient(135deg, #0a0b1a 0%, #1a1b2f 100%);
}

.bg-primary-gradient {
    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
}

.bg-dark-soft {
    background: rgba(255,255,255,0.05);
    backdrop-filter: blur(10px);
}

.metric-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
}

.metric-card {
    transition: all 0.3s ease;
    height: 100%;
}

.metric-card:hover {
    transform: translateY(-5px);
    background: rgba(255,255,255,0.1);
    border-color: var(--bs-primary) !important;
}

.metric-highlight {
    position: relative;
    max-width: 400px;
    box-shadow: 0 10px 20px rgba(148, 0, 255, 0.2);
}

@media (max-width: 768px) {
    .metric-grid {
        grid-template-columns: 1fr;
    }
    
    .metric-highlight {
        margin-left: auto;
        margin-right: auto;
    }
}
                    
            `}</style>
        </>
    );
}

export default PublisherSocialSchedulerPage;